import { Component, inject, input, OnInit, Signal, signal, ChangeDetectionStrategy } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { LoggingService } from '../../../logging/logging.service';
import { UserPreferencesService } from '../../../user/services/user-preferences/user-preferences.service';
import { User } from '../../../user/entities/user';
import { UserRoles } from '../../../user/entities/user-roles';
import { FrontendSetting, UserPreferences } from '../../../user/entities/user-preferences/user-preferences';
import { CurafidaCardComponent } from '../../../common/components/curafida-card/curafida-card.component';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
    CurafidaSelectInputComponent,
    SelectInputItem,
} from '../../../common/components/curafida-input/curafida-select-input/curafida-select-input.component';
import { IonCardContent, IonCheckbox, IonItem, IonLabel, IonRange } from '@ionic/angular';
import { CurafidaSaveCancelButtonComponent } from '../../../common/components/curafida-save-cancel-button/curafida-save-cancel-button.component';
import { ThemeService } from '../../../common/services/theme/theme.service';
import { ConfigService } from '../../services/config.service';
import { RoutingSegment } from '../../../common/entities/routing-segment';
import { Router } from '@angular/router';
import { StyleService } from '../../../common/services/style/style.service';
import { TranslatePipe } from '@ngx-translate/core';
import { CurafidaAuthService } from '../../../authentication/curafida-auth.service';
import { UserPreferencesDto } from '../../../user/entities/user-preferences/user-preferences.dto';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'user-preferences',
    templateUrl: './user-preferences.component.html',
    styleUrls: ['./user-preferences.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CurafidaCardComponent,
        ReactiveFormsModule,
        CurafidaSelectInputComponent,
        IonCardContent,
        CurafidaSaveCancelButtonComponent,
        IonLabel,
        IonRange,
        TranslatePipe,
        IonCheckbox,
        IonItem,
    ],
})
export class UserPreferencesComponent implements OnInit {
    readonly user = input<User>();
    resource: UserPreferences;
    protected readonly log = inject(LoggingService).getLogger(this.constructor.name);
    protected readonly userPreferencesService = inject(UserPreferencesService);
    protected readonly UserRoles = UserRoles;
    protected formUserPreferences: FormGroup;
    protected patientMode: SelectInputItem[] = [];
    protected isEditEnabled = false;
    protected configService = inject(ConfigService);
    protected isMobile: Signal<boolean> = signal<boolean>(undefined);
    private formBuilder = inject(FormBuilder);
    private themeService = inject(ThemeService);
    private router = inject(Router);
    private readonly styleService = inject(StyleService);
    private readonly authService = inject(CurafidaAuthService);

    async ngOnInit() {
        this.isMobile = this.styleService.isMobile;
        const enabledMode = this.configService.config()?.configuration.features.user?.patient?.enabledModus;
        if (enabledMode?.length > 0) {
            if (enabledMode.find((x) => x === 'MINI')) {
                this.patientMode.push({ value: 'MINI', label: 'MINIMAL_MODE' });
            }
        }
        if (this.configService.frontendConfig().features?.kiosk?.enabled) {
            this.patientMode.push({ value: 'KIOSK', label: 'KIOSK.SINGULAR' });
        }
        this.formUserPreferences = this.formBuilder.group({
            currentMode: new FormControl({ value: undefined, disabled: !this.isEditEnabled }),
            fontSize: new FormControl({ value: undefined, disabled: !this.isEditEnabled }),
            course: new FormControl({ value: undefined, disabled: !this.isEditEnabled }),
        });
        await this.initResource();
    }

    async initResource(): Promise<void> {
        try {
            this.resource = await this.userPreferencesService.getForUser(this.user().username);
            if (!this.resource.frontendSettings?.enabledSegments) {
                this.resource.frontendSettings = new FrontendSetting();
            }
            if (!this.resource.frontendSettings?.enabledSegments) {
                this.resource.frontendSettings.enabledSegments = [];
            }
            if (this.user().roles.includes(UserRoles.PATIENT)) {
                this.formUserPreferences.controls.currentMode.setValue(this.resource.currentMode);
            }
            this.formUserPreferences.controls.fontSize.setValue(this.resource?.frontendSettings?.fontSize ?? 14);
            if (!this.user().roles.includes(UserRoles.PATIENT)) {
                this.formUserPreferences.controls.course.setValue(
                    !!this.resource.frontendSettings.enabledSegments?.find((x) => x.name === 'course')?.status,
                );
            }
        } catch (err) {
            this.log.error((err as Error).message);
        }
    }

    /**
     * Changes the text size based on user input and updates the theme service.
     */
    changeTextSize(size: { detail: { value: number } }) {
        if (!this.resource.frontendSettings) {
            this.resource.frontendSettings = new FrontendSetting();
        }
        this.resource.frontendSettings.fontSize = size.detail.value ?? 14;
        if (this.user().username === this.authService.loggedInUser().username) {
            this.themeService.setTextSize(size.detail.value ?? 14);
        }
    }

    protected async saveForm() {
        this.toggleEdit();
        this.resource.currentMode = this.formUserPreferences.controls.currentMode.value;
        this.resource.frontendSettings.fontSize = this.formUserPreferences.controls.fontSize.value;
        if (!this.user().roles.includes(UserRoles.PATIENT)) {
            this.resource.frontendSettings.enabledSegments.find((x) => x.name === 'course').status =
                this.formUserPreferences.controls.course.value;
        }
        this.resource = await this.userPreferencesService.updateForUser(
            this.user().username,
            UserPreferencesDto.fromResource(this.resource),
        );

        if (
            this.resource?.currentMode === 'KIOSK' &&
            this.authService.loggedInUser().roles.includes(UserRoles.PATIENT)
        ) {
            await this.router.navigate([RoutingSegment.MEMBER, RoutingSegment.KIOSK]);
        }
    }

    protected toggleEdit() {
        this.isEditEnabled = !this.isEditEnabled;

        const currentMode = this.formUserPreferences.get('currentMode');
        const fontSize = this.formUserPreferences.get('fontSize');
        this.isEditEnabled ? currentMode.enable() : currentMode.disable();
        this.isEditEnabled ? fontSize.enable() : fontSize.disable();

        if (!this.user().roles.includes(UserRoles.PATIENT)) {
            const course = this.formUserPreferences.get('course');
            this.isEditEnabled ? course.enable() : course.disable();
        }
    }

    protected async cancelForm(): Promise<void> {
        this.toggleEdit();
        await this.initResource();
    }
}
